import React from "react";
import { ImageBackground, Pressable, View } from "react-native";
import ProgressBar from "react-native-progress/Bar";
import Text from "@/components/shared/AppText";
import { brandColor, buttonGray } from "../globalStyle";
import { bytesToSize } from "../globalUtils";
import AppActivityIndicator from "./AppActivityIndicator";
import AppTitle from "./AppTitle";
import AspectView from "./AspectView";
import { TrashCanButton, VideoUploadIcon } from "./Buttons";
import { ChoosePhotoIcon, UploadVideoIcon } from "./Buttons";
import FormSubtitle from "./FormSubtitle";
import tw from "twrnc";
import { t } from "@/localization";

const FileLoading = ({ loaded, total, progress }) => {
  const loadingTotal =
    loaded || total
      ? `${t("{loaded} from {total}", {
          loaded: bytesToSize(loaded),
          total: bytesToSize(total),
        })} `
      : "";

  const progressFormat = progress
    ? `${loadingTotal} (${Math.round(progress * 100)}%)`
    : t("Uploading...");

  return (
    <View style={tw`flex-1 justify-center items-center`}>
      <View style={tw`bg-white p-6 rounded-lg shadow-sm`}>
        <ProgressBar
          progress={progress}
          indeterminate={progress === 0}
          color="#F84123"
          style={tw`m-2.5`}
        />
        <Text>{progressFormat}</Text>
      </View>
    </View>
  );
};

export const EventPersonalizeUploadCardUI = ({
  event,
  highlighted,
  onChooseFile,
  loading,
  progress,
  loaded,
  total,
  onDeletePress,
  uploadButtonText,
  forceShowUploadForm,
  // Upload Service v2 only (undefined on the current upload path): the
  // fixed-size UploadProgressCard, an error under the prompt, the file hint.
  uploadPanel,
  errorText,
  fileHint,
}) => {
  const { video_uploaded, vod_filename, vod_converted, event_status } = event;

  const videoUploaded = forceShowUploadForm ? false : video_uploaded;
  const filename = (vod_filename || "").split("/").pop();

  return (
    <Pressable
      style={({ pressed }) => [
        tw`z-50`,
        { opacity: pressed || highlighted ? 0.7 : 1.0 },
      ]}
      onPress={onChooseFile}
    >
      <AspectView
        style={[
          tw`w-full mt-2.5 mb-1 border border-dashed border-[#A2A2A2] rounded items-center justify-center`,
          {
            aspectRatio: 1280 / 720,
          },
        ]}
      >
        {!!uploadPanel && (
          // Upload Service v2: the white card on the app's light grey, inside
          // the dashed border (the current path keeps its dark overlay below).
          <View
            testID="upload-panel-backdrop"
            style={[
              tw`absolute inset-0 z-50 rounded items-center justify-center`,
              { backgroundColor: buttonGray },
            ]}
          >
            {uploadPanel}
          </View>
        )}

        {!uploadPanel && ((!!progress && progress !== 1) || loading) && (
          <View
            style={tw`absolute inset-0 z-50 bg-black/50 items-center justify-center`}
          >
            <FileLoading loaded={loaded} progress={progress} total={total} />
          </View>
        )}

        {!uploadPanel && !loading && videoUploaded && (
          <View style={tw`w-full h-full flex-1 justify-center items-center`}>
            {!vod_converted && <AppActivityIndicator />}

            {vod_converted && <VideoUploadIcon />}

            {vod_converted ? (
              <Text
                style={tw`mt-4 text-base font-extrabold text-center tracking-normal`}
              >
                {t("Video Uploaded")}
              </Text>
            ) : (
              <Text
                style={tw`mt-4 text-base font-extrabold text-center tracking-normal`}
              >
                {t("Converting Video. This may take a few minutes.")}
              </Text>
            )}

            <Text
              style={tw`text-[#7f7f7f] text-sm font-medium text-center tracking-normal`}
            >
              {filename}
            </Text>
            <TrashCanButton onPress={onDeletePress} />
          </View>
        )}

        {!uploadPanel && !loading && !videoUploaded && (
          <>
            <Text>
              <UploadVideoIcon />
            </Text>
            <View style={tw`flex-row mt-1`}>
              <AppTitle
                h4
                text={
                  uploadButtonText ||
                  (event_status === "past"
                    ? t("Replace live video")
                    : t("Upload video"))
                }
              />
            </View>

            <FormSubtitle style={tw`text-sm mt-0.5 mb-0 p-1`}>
              {fileHint || t("MOV or MP4 video. 10GB Max.")}
            </FormSubtitle>
            {!!errorText && (
              <Text style={tw`text-sm text-center text-[#c74733] px-4`}>
                {errorText}
              </Text>
            )}
          </>
        )}
      </AspectView>
    </Pressable>
  );
};

export default EventPersonalizeUploadCardUI;
